import React, { useEffect } from 'react';
import { Field, FieldArray, Form, Formik, FormikProps } from 'formik';
import DetailsForm from 'components/DetailsPageBoilerplate/DetailsForm';
import { Heading } from 'lmc-ui-library-rebrand';
import './_Style.scss';
import {
  Details,
  SaveData,
  AdviserType,
  AdviserViewModel,
  BrokerDetailsDataStructure,
} from 'components/DetailsPageBoilerplate/types';
import { useDispatch } from 'react-redux';
import { resetContainerWidth, setContainerWidth } from 'store/layout/actions';
import { ContainerWidth } from 'store/layout/types';
import { ReducerDataState } from 'shared/interface/globalInterface';
import Spinner from 'shared/Spinner/Spinner';
import { addAdmin } from 'pages/Profile/utils/brokers';
import FormInputNewUi from 'shared/Inputs/Inline/FormInputNewUi';
import FormSelectNewUi from 'shared/Inputs/Inline/FormSelectNewUi';

type Props = {
  containerWidth: ContainerWidth;
  fetchedDetails: ReducerDataState<BrokerDetailsDataStructure>;
  onProfileUpdate: (values: SaveData) => void;
  removeAdminFromList: (name: string) => void;
};

const adviserDataToViewModel = (data: AdviserType): AdviserViewModel => {
  const details: Details[] = [
    { label: 'FCA No.', name: 'fca_number', value: data.fcaNumber },
    { label: 'Company', name: 'company', value: data.company },
    { label: 'Adviser Name', name: 'name', value: data.name },
  ];

  return {
    isSaved: false,
    error: '',
    details,
    data: {
      email: data.email,
      contactNumber: data.phone,
      isLMCActive: data.lmcStatus,
      id: data.id,
    },
  };
};

const renderDetails = (
  data: BrokerDetailsDataStructure,
  profileSubmitHandler: any,
  removeAdminFromList: (name: string) => void,
) => {
  const adviserViewModel: AdviserViewModel = adviserDataToViewModel(data.adviser);

  return (
    <>
      <div className="forms__column">
        <Heading mode="title" weight="bold" className="heading">
          Adviser Details
        </Heading>
        <DetailsForm
          details={adviserViewModel.details}
          data={adviserViewModel.data || {}}
          onSubmit={profileSubmitHandler()}
          error={adviserViewModel.error}
          isSaved={adviserViewModel.isSaved}
        />
      </div>
      <div className="forms__column">
        <Heading mode="title" weight="bold" className="heading">
          Adviser Support
        </Heading>
        <Formik initialValues={{ admins: data.admins }} onSubmit={() => {}} enableReinitialize={true}>
          {({ values, setFieldValue, setFieldError, errors }: FormikProps<any>) => (
            <Form className="form admin-details">
              <FieldArray
                name={'admins'}
                render={(arrayHelpers) => (
                  <>
                    <Field
                      name="availableAdmin"
                      component={FormSelectNewUi}
                      label="Add support"
                      options={data.availableAdmins.map((availableAdmin) => ({
                        id: availableAdmin.id,
                        value: availableAdmin.name,
                        option: availableAdmin.name,
                      }))}
                      onClick={async () => {
                        try {
                          await addAdmin(values.availableAdmin.id);
                          removeAdminFromList(values.availableAdmin.value);
                          arrayHelpers.push({ name: values.availableAdmin.value });
                          setFieldValue('availableAdmin', '');
                        } catch (err) {
                          setFieldError('availableAdmin', 'Something went wrong');
                        }
                      }}
                      buttonClass="button--newUi"
                      disableButton={!values.availableAdmin}
                      error={errors['availableAdmin']}
                    />
                    {values.admins.map((admin: any, index: number) => (
                      <Field
                        key={admin.name}
                        name={`admins.${index}.name`}
                        component={FormInputNewUi}
                        label="Adviser Support"
                        readOnly={true}
                      />
                    ))}
                  </>
                )}
              />
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

const BrokerProfileDetails: React.FC<Props> = ({
  fetchedDetails,
  containerWidth,
  onProfileUpdate,
  removeAdminFromList,
}) => {
  const dispatch = useDispatch();
  const data: BrokerDetailsDataStructure = fetchedDetails.data;

  useEffect(() => {
    dispatch(setContainerWidth(containerWidth));

    return () => {
      dispatch(resetContainerWidth());
    };
  }, [containerWidth, dispatch]);

  const profileSubmitHandler = () => {
    return (values: SaveData) => {
      onProfileUpdate(values);
    };
  };

  return (
    <div className="forms">
      {fetchedDetails.pending && (
        <div>
          <Spinner />
        </div>
      )}
      {fetchedDetails.error && <div>{fetchedDetails.error}</div>}
      {!fetchedDetails.pending &&
        !fetchedDetails.error &&
        renderDetails(data, profileSubmitHandler, removeAdminFromList)}
    </div>
  );
};

export default BrokerProfileDetails;
